import {
    closestCenter,
    DndContext,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useEffect, useRef, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import axios from 'axios';
import MainFieldConfigModal from './MainFieldConfigModal';
import './MainSearchFields.css';
import SortableMainFieldItem from './SortableMainFieldItem';

const MainSearchFieldsDragDrop = ({ showToast, onFieldsChange, initialFields = [] }) => {
  const [searchFields, setSearchFields] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingField, setEditingField] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  
  // Config modal state
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configField, setConfigField] = useState(null);
  
  const isSavingRef = useRef(false);
  const pendingSaveRef = useRef(null);

  const API_BASE = window.HavenlyticsData?.hvnlyNabApi?.url || '/wp-json/hvnlynab/v1';
  const API_NONCE = window.HavenlyticsData?.hvnlyNabApi?.nonce || '';
  const SETTINGS_ENDPOINT = `${API_BASE}/plugin-settings/search`;

  const AXIOS_CONFIG = {
    headers: {
      'X-WP-Nonce': API_NONCE,
      'Content-Type': 'application/json'
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor)
  );

  const DEFAULT_MAIN_FIELDS = [
    {
      id: 'keyword_search',
      title: __('Keyword Search', 'havenlytics'),
      type: 'text',
      enabled: true,
      order: 1,
      is_default: true,
      config: {
        placeholder: __('Search Address', 'havenlytics'),
        isAjax: true,
      }
    },
    {
      id: 'property_type',
      title: __('Property Type', 'havenlytics'),
      type: 'taxonomy',
      enabled: true,
      order: 2,
      is_default: true,
      config: {
        taxonomy: 'hvnly_prop_types',
        placeholder: __('Property Type', 'havenlytics'),
        useTaxonomy: true,
      }
    },
    {
      id: 'location',
      title: __('Location', 'havenlytics'),
      type: 'taxonomy',
      enabled: true,
      order: 3,
      is_default: true,
      config: {
        taxonomy: 'hvnly_prop_locations',
        placeholder: __('Search Location', 'havenlytics'),
        useTaxonomy: true,
      }
    },
  ];

  useEffect(() => {
    if (initialFields && initialFields.length > 0) {
      const sortedFields = [...initialFields].sort((a, b) => (a.order || 0) - (b.order || 0));
      setSearchFields(sortedFields);
      setIsLoading(false);
    } else {
      loadMainSearchFields();
    }
  }, [initialFields]);

  const loadMainSearchFields = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(SETTINGS_ENDPOINT, AXIOS_CONFIG);
      if (response.data?.success && response.data?.data) {
        const searchData = response.data.data;
        let fields = searchData.hvnly_main_search_fields || [];
        
        if (fields.length > 0) {
          fields.sort((a, b) => (a.order || 0) - (b.order || 0));
          setSearchFields(fields);
          if (onFieldsChange) {
            onFieldsChange(fields);
          }
        } else {
          setSearchFields([...DEFAULT_MAIN_FIELDS]);
        }
      } else {
        setSearchFields([...DEFAULT_MAIN_FIELDS]);
      }
    } catch (error) {
      console.error('Failed to load main search fields:', error);
      setSearchFields([...DEFAULT_MAIN_FIELDS]);
      if (showToast) {
        showToast('warning', __('Notice', 'havenlytics'), __('Using default main search fields', 'havenlytics'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const saveMainSearchFields = async (fields) => {
    if (isSavingRef.current) {
      pendingSaveRef.current = fields;
      return;
    }
    
    isSavingRef.current = true;
    
    try {
      const response = await axios.get(SETTINGS_ENDPOINT, AXIOS_CONFIG);
      const currentSettings = response.data?.data || {};
      
      const updatedSettings = { ...currentSettings };
      updatedSettings.hvnly_main_search_fields = fields;
      
      const saveResponse = await axios.post(SETTINGS_ENDPOINT, updatedSettings, AXIOS_CONFIG);
      
      if (saveResponse.data?.success) {
        if (onFieldsChange) {
          onFieldsChange(fields);
        }
        
        if (pendingSaveRef.current) {
          const pending = pendingSaveRef.current;
          pendingSaveRef.current = null;
          setTimeout(() => saveMainSearchFields(pending), 100);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to save main search fields:', error);
      if (showToast) {
        showToast('error', __('Error', 'havenlytics'), __('Failed to save main search fields', 'havenlytics'));
      }
      return false;
    } finally {
      isSavingRef.current = false;
    }
  };

  const moveFieldUp = (id) => {
    setSearchFields(prev => {
      const index = prev.findIndex(field => field.id === id);
      if (index === 0) return prev;
      
      const newFields = [...prev];
      [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
      newFields.forEach((field, idx) => { field.order = idx + 1; });
      setTimeout(() => saveMainSearchFields(newFields), 0);
      return newFields;
    });
  };

  const moveFieldDown = (id) => {
    setSearchFields(prev => {
      const index = prev.findIndex(field => field.id === id);
      if (index === prev.length - 1) return prev;
      
      const newFields = [...prev];
      [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
      newFields.forEach((field, idx) => { field.order = idx + 1; });
      setTimeout(() => saveMainSearchFields(newFields), 0);
      return newFields;
    });
  };

  const toggleField = (id) => {
    setSearchFields(prev => {
      const newFields = prev.map(field => 
        field.id === id ? { ...field, enabled: !field.enabled } : field
      );
      setTimeout(() => saveMainSearchFields(newFields), 0);
      return newFields;
    });
  };

  const startEditing = (field) => {
    setEditingField(field.id);
    setEditTitle(field.title);
  };

  const saveEdit = () => {
    if (!editTitle.trim()) {
      if (showToast) {
        showToast('error', __('Error', 'havenlytics'), __('Field title cannot be empty', 'havenlytics'));
      }
      return;
    }
    
    setSearchFields(prev => {
      const newFields = prev.map(field => 
        field.id === editingField ? { ...field, title: editTitle } : field
      );
      setTimeout(() => saveMainSearchFields(newFields), 0);
      return newFields;
    });
    setEditingField(null);
    if (showToast) {
      showToast('success', __('Success', 'havenlytics'), __('Field title updated!', 'havenlytics'));
    }
  };

  const cancelEdit = () => {
    setEditingField(null);
  };

  const handleConfigureField = (field) => {
    setConfigField(field);
    setConfigModalOpen(true);
  };

  const handleSaveFieldConfig = async (updatedField) => {
    setSearchFields(prev => {
      const newFields = prev.map(field => 
        field.id === updatedField.id ? updatedField : field
      );
      setTimeout(() => saveMainSearchFields(newFields), 0);
      return newFields;
    });
    showToast('success', __('Success', 'havenlytics'), __('Field configuration saved!', 'havenlytics'));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      setSearchFields((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        const newItems = [...items];
        const [movedItem] = newItems.splice(oldIndex, 1);
        newItems.splice(newIndex, 0, movedItem);
        newItems.forEach((item, index) => { item.order = index + 1; });
        setTimeout(() => saveMainSearchFields(newItems), 0);
        return newItems;
      });
    }
  };

  const getTypeLabel = (type) => {
    const types = {
      'text': __('Text', 'havenlytics'),
      'taxonomy': __('Taxonomy', 'havenlytics'),
    };
    return types[type] || type;
  };

  if (isLoading) {
    return (
      <div className="hvnly_main_search_fields_drag_drop_builder">
        <div className="hvnly_main_search_fields_drag_drop_loading">
          <i className="fas fa-spinner fa-spin"></i>
          <p>{__('Loading main search fields...', 'havenlytics')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hvnly_main_search_fields_drag_drop_builder">
      <div className="hvnly_main_search_fields_drag_drop_header">
        <i className="fas fa-search"></i>
        <span>{__('Main Search Fields', 'havenlytics')}</span>
        <span className="hvnly_main_search_fields_drag_drop_hint">
          {__('Drag to reorder | Click Edit to rename | Click Configure to set options', 'havenlytics')}
        </span>
      </div>
      
      <div className="hvnly_main_search_fields_drag_drop_body">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={searchFields.map(f => f.id)} strategy={verticalListSortingStrategy}>
            <div className="hvnly_main_search_fields_drag_drop_list">
              {searchFields.length > 0 ? (
                searchFields.map((field, index) => (
                  <SortableMainFieldItem
                    key={field.id}
                    field={{...field, typeLabel: getTypeLabel(field.type)}}
                    index={index}
                    onMoveUp={moveFieldUp}
                    onMoveDown={moveFieldDown}
                    onToggle={toggleField}
                    onEdit={startEditing}
                    onConfigure={handleConfigureField}
                    isEditing={editingField === field.id}
                    editTitle={editTitle}
                    onEditTitleChange={setEditTitle}
                    onSaveEdit={saveEdit}
                    onCancelEdit={cancelEdit}
                    fieldsLength={searchFields.length}
                    isDefault={field.is_default}
                  />
                ))
              ) : (
                <p style={{ textAlign: 'center', padding: '40px' }}>
                  {__('No main search fields found.', 'havenlytics')}
                </p>
              )}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      <MainFieldConfigModal
        isOpen={configModalOpen}
        onClose={() => {
          setConfigModalOpen(false);
          setConfigField(null);
        }}
        field={configField}
        onSave={handleSaveFieldConfig}
        showToast={showToast}
      />
    </div>
  );
};

export default MainSearchFieldsDragDrop;